import * as React from 'react'
import { useState, useCallback, useRef } from 'react'
import { twMerge } from 'tailwind-merge'
import { Plus } from 'lucide-react'

interface QuickAddInputProps {
  sectionName: string
  onAdd: (sectionName: string, itemText: string) => void
  className?: string
}

export function QuickAddInput({ sectionName, onAdd, className }: QuickAddInputProps) {
  const [value, setValue] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  const handleSubmit = useCallback(() => {
    if (value.trim()) {
      onAdd(sectionName, value.trim())
      setValue('')
    }
  }, [value, sectionName, onAdd])

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        e.stopPropagation()
        handleSubmit()
      } else if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        setValue('')
      }
    },
    [handleSubmit],
  )

  const handleRowClick = useCallback((e: React.MouseEvent) => {
    e.stopPropagation()
    inputRef.current?.focus()
  }, [])

  return (
    <div className={twMerge('flex w-full cursor-text items-center gap-2.5', className)} onClick={handleRowClick}>
      <div className="bg-light/5 relative inline-flex size-[22px] flex-shrink-0 items-center justify-center rounded-full border">
        <Plus strokeWidth={3} className="text-light/40 size-4" />
      </div>
      <input
        ref={inputRef}
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Add todo item..."
        className={twMerge(
          'flex-1 border-none bg-transparent px-0 transition-opacity',
          'text-light/60 placeholder:text-light/20 text-[13px]',
          'transition-colors',
          'focus:text-light/80 focus:placeholder:text-light/30',
        )}
      />
    </div>
  )
}
